import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Alert,
  Stack,
  Divider,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  ExpandMore,
  Edit,
  Check,
  Close,
  ContentCopy,
  Download,
  Refresh,
  AutoFixHigh,
  TextFields,
  TableChart,
  Label,
} from '@mui/icons-material';
import { ExtractionResult } from '../services/aiProcessingService';

interface ExtractionInterfaceProps {
  extractionResult?: ExtractionResult;
  onCorrection?: (field: string, oldValue: string, newValue: string) => void;
  onReprocess?: (options: any) => void;
  loading?: boolean;
}

export const ExtractionInterface: React.FC<ExtractionInterfaceProps> = ({
  extractionResult,
  onCorrection,
  onReprocess,
  loading = false,
}) => {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showRawData, setShowRawData] = useState(false);
  const [reprocessOptions, setReprocessOptions] = useState({
    useGemma: true,
    useTesseract: true,
    enhance: false,
    language: 'eng',
  });
  const [reprocessDialogOpen, setReprocessDialogOpen] = useState(false);

  const handleEditStart = (field: string, currentValue: string) => {
    setEditingField(field);
    setEditValue(currentValue);
  };

  const handleEditSave = () => {
    if (editingField && onCorrection) {
      const oldValue = getFieldValue(editingField);
      onCorrection(editingField, oldValue, editValue);
    }
    setEditingField(null);
    setEditValue('');
  };

  const handleEditCancel = () => {
    setEditingField(null);
    setEditValue('');
  };

  const getFieldValue = (field: string): string => {
    if (!extractionResult) return '';
    
    const parts = field.split('.');
    let value: any = extractionResult;
    
    for (const part of parts) {
      value = value?.[part];
    }
    
    return String(value || '');
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const downloadAsJson = () => {
    if (!extractionResult) return;
    
    const blob = new Blob([JSON.stringify(extractionResult, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `extraction-${extractionResult.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleReprocess = () => {
    if (onReprocess) {
      onReprocess(reprocessOptions);
    }
    setReprocessDialogOpen(false);
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Processing Document...</Typography>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary">
            Running AI models to extract data from your document
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (!extractionResult) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="text.secondary">
          No extraction results available. Upload and process a document to see results here.
        </Typography>
      </Paper>
    );
  }

  const { gemmaResult, tesseractResult, status, errors, processingTime } = extractionResult;

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      <Stack spacing={2}>
        {/* Status Bar */}
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Chip
              label={status}
              color={status === 'success' ? 'success' : status === 'partial' ? 'warning' : 'error'}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              Processed in {(processingTime / 1000).toFixed(2)}s
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              startIcon={<Refresh />}
              size="small"
              onClick={() => setReprocessDialogOpen(true)}
            >
              Reprocess
            </Button>
            <Button
              startIcon={<Download />}
              size="small"
              onClick={downloadAsJson}
            >
              Export
            </Button>
          </Stack>
        </Paper>

        {/* Errors */}
        {errors && errors.length > 0 && (
          <Alert severity="error">
            <Typography variant="subtitle2">Processing Errors:</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Combined Text */}
        {extractionResult.combinedText && (
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <TextFields sx={{ mr: 1 }} />
              <Typography>Extracted Text</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="flex-end">
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(extractionResult.combinedText!)}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Stack>
                    <TextField
                      multiline
                      fullWidth
                      value={extractionResult.combinedText}
                      InputProps={{ readOnly: true }}
                      minRows={4}
                      maxRows={10}
                    />
                  </Stack>
                </CardContent>
              </Card>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Entities */}
        {gemmaResult?.entities && gemmaResult.entities.length > 0 && (
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Label sx={{ mr: 1 }} />
              <Typography>Extracted Entities</Typography>
              <Chip
                label={gemmaResult.entities.length}
                size="small"
                sx={{ ml: 1 }}
              />
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {gemmaResult.entities.map((entity, idx) => {
                  const fieldKey = `gemmaResult.entities.${idx}.value`;
                  const isEditing = editingField === fieldKey;
                  
                  return (
                    <ListItem key={idx} divider>
                      <ListItemText
                        primary={
                          isEditing ? (
                            <TextField
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              size="small"
                              fullWidth
                              autoFocus
                            />
                          ) : (
                            entity.value
                          )
                        }
                        secondary={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip label={entity.type} size="small" />
                            <Typography variant="caption">
                              {(entity.confidence * 100).toFixed(0)}% confidence
                            </Typography>
                          </Stack>
                        }
                      />
                      <ListItemSecondaryAction>
                        {isEditing ? (
                          <>
                            <IconButton size="small" onClick={handleEditSave}>
                              <Check />
                            </IconButton>
                            <IconButton size="small" onClick={handleEditCancel}>
                              <Close />
                            </IconButton>
                          </>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => handleEditStart(fieldKey, entity.value)}
                          >
                            <Edit />
                          </IconButton>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Tables */}
        {gemmaResult?.tables && gemmaResult.tables.length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <TableChart sx={{ mr: 1 }} />
              <Typography>Extracted Tables</Typography>
              <Chip
                label={gemmaResult.tables.length}
                size="small"
                sx={{ ml: 1 }}
              />
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">
                Table extraction will be displayed here
              </Typography>
            </AccordionDetails>
          </Accordion>
        )}

        {/* OCR Details */}
        {tesseractResult && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <AutoFixHigh sx={{ mr: 1 }} />
              <Typography>OCR Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Confidence
                    </Typography>
                    <Typography variant="h6">
                      {tesseractResult.confidence.toFixed(1)}%
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Text Blocks
                    </Typography>
                    <Typography variant="h6">
                      {tesseractResult.blocks.length}
                    </Typography>
                  </Box>
                </Box>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={showRawData}
                      onChange={(e) => setShowRawData(e.target.checked)}
                    />
                  }
                  label="Show raw OCR data"
                />
                
                {showRawData && (
                  <Card variant="outlined">
                    <CardContent>
                      <pre style={{ overflow: 'auto', fontSize: '0.75rem' }}>
                        {JSON.stringify(tesseractResult, null, 2)}
                      </pre>
                    </CardContent>
                  </Card>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Metadata */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Metadata</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Extraction ID"
                  secondary={extractionResult.id}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Document ID"
                  secondary={extractionResult.documentId}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Timestamp"
                  secondary={extractionResult.timestamp.toLocaleString()}
                />
              </ListItem>
              {gemmaResult?.metadata && (
                <ListItem>
                  <ListItemText
                    primary="Model Version"
                    secondary={gemmaResult.metadata.modelVersion}
                  />
                </ListItem>
              )}
            </List>
          </AccordionDetails>
        </Accordion>
      </Stack>

      {/* Reprocess Dialog */}
      <Dialog
        open={reprocessDialogOpen}
        onClose={() => setReprocessDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reprocess Document</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={reprocessOptions.useGemma}
                  onChange={(e) =>
                    setReprocessOptions({
                      ...reprocessOptions,
                      useGemma: e.target.checked,
                    })
                  }
                />
              }
              label="Use Gemma 3n AI Model"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={reprocessOptions.useTesseract}
                  onChange={(e) =>
                    setReprocessOptions({
                      ...reprocessOptions,
                      useTesseract: e.target.checked,
                    })
                  }
                />
              }
              label="Use Tesseract OCR"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={reprocessOptions.enhance}
                  onChange={(e) =>
                    setReprocessOptions({
                      ...reprocessOptions,
                      enhance: e.target.checked,
                    })
                  }
                />
              }
              label="Enhance image for better OCR"
            />
            
            <FormControl fullWidth>
              <InputLabel>OCR Language</InputLabel>
              <Select
                value={reprocessOptions.language}
                label="OCR Language"
                onChange={(e) =>
                  setReprocessOptions({
                    ...reprocessOptions,
                    language: e.target.value,
                  })
                }
              >
                <MenuItem value="eng">English</MenuItem>
                <MenuItem value="spa">Spanish</MenuItem>
                <MenuItem value="fra">French</MenuItem>
                <MenuItem value="deu">German</MenuItem>
                <MenuItem value="chi_sim">Chinese (Simplified)</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReprocessDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReprocess} variant="contained">
            Reprocess
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};